/**
 * useChatStream — extracted SSE consumer for Ask Argus.
 *
 * Wave 1.5 of the chat-ui-improvement program (`docs/CHAT-UI-IMPROVEMENT-DESIGN.md` §2.1).
 *
 * This hook owns the in-flight stream state for an Ask Argus turn:
 *   - AbortController lifecycle (created on send, exposed via `abort()`)
 *   - `fetch()` + ReadableStream parser loop over `data:` lines
 *   - Per-agent state Maps (multi-agent dispatch via `agent_id` on every SSE event)
 *   - Phase machine via `reduceStreamPhase` (idle / awaiting / tool_calling /
 *     streaming / done / error)
 *   - Safety timeout (default 180s — #564 fix) with `_timedOut` distinct error
 *     rendering
 *   - Client-side reconnect machinery (Q2: `Last-Event-ID` header +
 *     1s/2s/4s exponential backoff). The mechanism is preserved, but
 *     the default attempt-count is now 0 (fail-closed) per issue #1323
 *     — the server does NOT support true SSE resume, so a reconnect
 *     re-runs the whole query and re-emits every token, which the
 *     client appended into the same message bubble (the `BLUEBLUE`
 *     regression class). Callers (mainly the reconnect-loop unit
 *     tests) can re-enable per-instance via `maxReconnectAttempts: N`.
 *     The default constant and its rationale live next to
 *     `DEFAULT_MAX_RECONNECT_ATTEMPTS` below.
 *
 * What the hook DOES NOT own (stays in the route):
 *   - `messages` / `setMessages` state
 *   - Conversation create/load lifecycle, history POSTs, feedback POSTs
 *   - Attached-highlights folding
 *   - Agent presentation resolution
 *
 * The route's `useEffect` glue updates `messages` based on the hook's
 * lifecycle callbacks (`onMetadata`, `onToken`, `onToolCall`, `onCitation`,
 * `onConfidence`, `onError`, `onDone`) plus the public `agentBubbles` Map.
 *
 * Behavior preservation (verified against `routes/ask-argus/index.tsx`
 * lines 334-897 at commit 08a38e2c0):
 *   - Plain `fetch` with bearer token from `localStorage` (apiClient
 *     cannot consume SSE bodies).
 *   - `\n\n`-split parser loop over `data:` lines.
 *   - `done` event immediately fires `onDone` and sets `isStreaming(false)`
 *     (PR fix #1054 — Strix Halo "several-second lag").
 *   - Multi-agent bubble materialization on each unseen `agent_id`. Treats
 *     `system.ask_argus` and `ask-argus` as "no agent" (default bubble).
 *   - AbortController catches on the parser loop produce `_Generation
 *     stopped._` suffix on the most-recently materialized agent's content.
 *   - 180s safety timeout aborts the controller with a `_timedOut` flag so
 *     the catch path can render a distinct SSE_TIMEOUT error.
 *
 * Architecture: `ChatStreamController` (pure, non-React) holds all
 * dispatch logic + the parser loop. The React hook is a thin shell
 * that wires React state to the controller's emitter callbacks. This
 * split lets the controller be unit-tested without DOM/jsdom (matches
 * the project convention in `use-argus-live.grants.test.ts`).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  reduceStreamPhase,
  extractToggles,
  extractConfidence,
  extractCitations,
  extractPipelinePhaseLabel,
  normalizeErrorEvent,
  type StreamPhase,
  type ChatToggles,
  type ChatErrorState,
  type ConfidenceChipState,
} from '../chat-stream-state';
import type { Citation, ToolCall } from '../chat-types';
import { ensureFreshAccessToken, handleStreamUnauthorized } from '../api-client';
import { EXPORT_CONTROLS_BY_STRICTNESS, SENSITIVITIES } from '@argusai/shared';

// ── Public types ────────────────────────────────────────────────────────────

/**
 * SSE endpoint accepted by the hook. The project chat fork (and any
 * helper-chat variant) point at `/studio/helper/chat`; the canonical Ask
 * Argus chat points at `/ecosystem/query`. Anything else is a 404 — keep
 * the type narrow on purpose so callers can't typo.
 */
export type StreamEndpoint = '/ecosystem/query' | '/studio/helper/chat';

/**
 * Reconnect state for the client-side retry machine. UI surfacing of
 * this field is gated to Wave 3B per the founder decision; the hook
 * still tracks it accurately in v1 so the wire-up later is mechanical.
 */
export type ReconnectState = 'idle' | 'reconnecting' | 'failed';

/**
 * Submit context the caller passes alongside the user's text. Shape
 * matches the existing `ChatSubmitContext` from `chat-input.helpers.ts`
 * (agent / model / thinkingMode / toggles) plus everything the route
 * uses today to compose the wire payload: `conversationId`, optional
 * `attachedToolsSnapshot`, optional `extraBody` escape hatch.
 *
 * The hook does NOT mutate this object — it reads fields and folds them
 * into the POST body. Anything the caller wants to thread that this
 * shape doesn't model goes into `extraBody`.
 */
export interface ChatSubmitContext {
  /** Conversation UUID the turn belongs to. */
  conversationId: string;
  /** Selected agent slug or id (composer's @mention / picker). */
  agentId?: string | null;
  /** Selected brand / model id (composer's model picker). */
  brand?: string | null;
  /** Per-turn toggle state (already-serialized for the wire). */
  toggles?: Record<string, unknown>;
  /**
   * Attached-tools snapshot — kept for forward-compat with backends
   * that want to accept the tool set on each turn. The current route
   * does not forward this, but Wave 2+ pods may.
   */
  attachedToolsSnapshot?: unknown;
  /**
   * Escape hatch for fields the canonical context shape doesn't cover.
   * Merged into the POST body verbatim — caller is responsible for
   * collision-free key choice.
   */
  extraBody?: Record<string, unknown>;
}

/**
 * Per-agent stream state. The hook exposes the live Map via
 * `agentBubbles` on the result; callers consume it inside an effect
 * keyed on `agentBubblesVersion` (the hook bumps a counter on every
 * mutation, so a Map-identity equality check isn't needed).
 *
 * The default bubble (no-agent / `system.ask_argus` / `ask-argus`) is
 * stored under the literal key `'__default__'`.
 */
export interface AgentBubbleState {
  /**
   * Bubble key — `__default__` for the no-agent / canonical Ask Argus
   * bubble, or the raw `agent_id` for a materialized specialist bubble.
   */
  key: string;
  /** Stable placeholder id the route uses to attach the bubble to its message list. */
  bubbleId: string;
  /** Original `agent_id` from the SSE event (undefined for default). */
  agentId: string | undefined;
  /** Display name from the dispatcher's metadata event, when present. */
  displayName: string | undefined;
  /** Display kind from the dispatcher's metadata event, when present. */
  kind: string | undefined;
  /** Accumulated assistant content for this bubble. */
  content: string;
  /** Citations accumulated for this bubble (in arrival order). */
  citations: Citation[];
  /** Tool calls accumulated for this bubble (in arrival order). */
  toolCalls: ToolCall[];
  /** Confidence score from the most recent `confidence` event. */
  confidence: number | undefined;
  /** Rationale from the most recent `confidence` event. */
  confidenceRationale: string | undefined;
  /** Metadata accumulated for this bubble. */
  metadata: {
    model?: string;
    intent?: string;
    totalTokens?: number;
    latencyMs?: number;
    [k: string]: unknown;
  };
  /** Terminal error for this bubble, if one was reported. */
  error: ChatErrorState | undefined;
  /** Last SSE `event_id` we've observed on this bubble (for resume). */
  lastEventId: string | undefined;
}

/**
 * Options accepted by both the controller and the React hook wrapper.
 */
export interface UseChatStreamOptions {
  /** Endpoint to POST against. */
  endpoint: StreamEndpoint;
  /**
   * Function that returns the current auth bearer token (e.g.
   * `() => localStorage.getItem('accessToken')`). Read on every send
   * so token refresh between turns is picked up.
   */
  authToken: () => string | null;

  // ── Lifecycle callbacks ──────────────────────────────────────────────────

  onAgentBubble?: (info: {
    bubbleId: string;
    bubbleKey: string;
    agentId: string | undefined;
    displayName: string | undefined;
    kind: string | undefined;
    retryPrompt: string;
  }) => void;

  onMetadata?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    agentId: string | undefined;
    model: string | undefined;
    intent: string | undefined;
    displayName: string | undefined;
    kind: string | undefined;
    classification:
      | { sensitivity: string; exportControl: string }
      | undefined;
    toggles: ChatToggles | undefined;
  }) => void;

  onToken?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    agentId: string | undefined;
    delta: string;
    fullContent: string;
  }) => void;

  onToolCall?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    agentId: string | undefined;
    toolCall: ToolCall | Record<string, unknown>;
  }) => void;

  onCitation?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    agentId: string | undefined;
    citations: Citation[];
  }) => void;

  onConfidence?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    agentId: string | undefined;
    confidence: number | undefined;
    rationale: string | undefined;
    chip?: ConfidenceChipState;
  }) => void;

  onPipeline?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    stages: unknown[];
  }) => void;

  /** bug #13 — answer-quality banner ("not independently verified"). */
  onBanner?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    level: string;
    kind: string;
    message: string;
  }) => void;

  /** bug #23 — proactive follow-up suggestions emitted after the answer. */
  onFollowUp?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    suggestions: string[];
  }) => void;

  onError?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    agentId: string | undefined;
    error: ChatErrorState;
    /**
     * True when this was the safety timeout firing, false otherwise.
     * Lets the route render a "Response timed out" toast distinct from
     * a server-emitted error.
     */
    timedOut: boolean;
  }) => void;

  onDone?: (evt: {
    bubbleId: string;
    bubbleKey: string;
    agentId: string | undefined;
    totalTokens: number | undefined;
    latencyMs: number | undefined;
    /**
     * Row id of the persisted assistant message, when the server reports one.
     * The streamed bubble's own id is a synthetic local string, which the
     * feedback endpoint rejects — so thumbs up/down needs this to act on the
     * turn the user just read.
     */
    messageId: string | undefined;
  }) => void;

  /**
   * Fires when the user aborts the in-flight stream via `abort()`.
   * The route renders `_Generation stopped._` on whichever bubble was
   * actively streaming.
   */
  onAbort?: (evt: { bubbleId: string; bubbleKey: string }) => void;

  /** Fires whenever the reconnect machine transitions. */
  onReconnectState?: (next: ReconnectState, attempt: number) => void;

  /**
   * Fires whenever the controller's emitter state changes — used by
   * the React hook to bump its render version. Pure controller users
   * (e.g. tests) can ignore this.
   */
  onStateChange?: (snapshot: ChatStreamSnapshot) => void;

  /** Default 180_000 ms. */
  safetyTimeoutMs?: number;

  /** Default 3. */
  maxReconnectAttempts?: number;

  /**
   * Test seam — defaults to global `fetch`. Tests substitute a controllable mock.
   */
  fetchImpl?: typeof fetch;
}

/**
 * Public hook surface — returned to the route.
 */
export interface UseChatStreamResult {
  /** Current SSE phase. Drives ArgusEye animation. */
  phase: StreamPhase;
  /** User-facing label from the latest backend pipeline progress event. */
  activePipelinePhaseLabel: string | undefined;
  /** True from `send()` until the parser loop terminates. */
  isStreaming: boolean;
  /** Toggles captured from the most recent metadata event. */
  activeToggles: ChatToggles | undefined;
  /** Classification captured from the most recent metadata event. */
  activeClassification:
    | { sensitivity: string; exportControl: string }
    | undefined;
  /** Live per-agent state Map. Republished by version-bump on mutation. */
  agentBubbles: ReadonlyMap<string, Readonly<AgentBubbleState>>;
  /** Current reconnect machine state. */
  reconnectState: ReconnectState;
  /** Submit a turn. */
  send: (text: string, ctx: ChatSubmitContext) => Promise<void>;
  /** Abort the in-flight stream. Idempotent. */
  abort: () => void;
}

/**
 * Snapshot of controller state — emitted on every mutation. Used by
 * the React hook to drive setState; testable users can ignore it.
 */
export interface ChatStreamSnapshot {
  phase: StreamPhase;
  activePipelinePhaseLabel: string | undefined;
  isStreaming: boolean;
  activeToggles: ChatToggles | undefined;
  activeClassification:
    | { sensitivity: string; exportControl: string }
    | undefined;
  agentBubbles: ReadonlyMap<string, Readonly<AgentBubbleState>>;
  reconnectState: ReconnectState;
}

// ── Internal helpers ────────────────────────────────────────────────────────

/**
 * Resolve the per-event bubble key. The dispatcher tags every SSE
 * event with `agent_id`; missing tag = default Ask Argus bubble. The
 * canonical Ask Argus slugs are also treated as "no agent" so they
 * group with the default bubble.
 */
export function resolveBubbleKey(rawAgentId: string | undefined): string {
  if (!rawAgentId) return '__default__';
  if (rawAgentId === 'system.ask_argus' || rawAgentId === 'ask-argus') {
    return '__default__';
  }
  return rawAgentId;
}

/**
 * Extract `agent_id` from either the event root or `event.data` —
 * dispatcher emits it on both shapes across `/ecosystem/query` and
 * `/studio/helper/chat`. Returns undefined when the event carries no
 * tag (legacy events fall through to the default bubble).
 */
export function extractAgentId(
  evt: Record<string, unknown>,
): string | undefined {
  const top = evt.agent_id;
  if (typeof top === 'string') return top;
  const data = evt.data as Record<string, unknown> | undefined;
  if (data && typeof data === 'object') {
    const nested = data.agent_id;
    if (typeof nested === 'string') return nested;
  }
  return undefined;
}

/**
 * Extract a stable event id from an SSE payload. The hook stores the
 * most recent `event_id` per bubble so `Last-Event-ID` can be sent on
 * reconnect (the server may or may not respect it in v1 — see
 * `useChatStream` JSDoc).
 */
export function extractEventId(
  evt: Record<string, unknown>,
): string | undefined {
  if (typeof evt.event_id === 'string') return evt.event_id;
  if (typeof evt.id === 'string') return evt.id;
  const data = evt.data as Record<string, unknown> | undefined;
  if (data && typeof data === 'object') {
    if (typeof data.event_id === 'string') return data.event_id;
    if (typeof data.id === 'string') return data.id;
  }
  return undefined;
}

/**
 * Sanitize a non-OK response status into a user-visible error message.
 * Identical wording to the existing route fix #745 — backend stack
 * traces are stripped.
 */
/**
 * Read the reason out of a pre-stream refusal our own API authored.
 *
 * Foundations 1 / B0-T1 review R2. `POST /ecosystem/query` answers a web turn
 * on a tenant whose `allow_web_search` is off with a plain JSON
 * `403 { error: { code: 'WEB_SEARCH_DISABLED', message: 'Web search is turned
 * off for your organization…' } }` — deliberately BEFORE `reply.hijack()`, "so
 * the user gets a reason". The client used to map every non-2xx to
 * `HTTP_<status>` and the reason never rendered; the refusal was not true
 * end-to-end.
 *
 * The rules are deliberately narrow, because #745 (no backend detail on
 * screen) still stands:
 *   - 4xx only. A 5xx body can carry a stack trace; those keep the sanitized
 *     wording.
 *   - the code must look like an API error code (SCREAMING_SNAKE), which the
 *     routes' `ErrorSchema` always is, and never like prose.
 *   - the message must be short, single-line prose — not a payload dump.
 * Anything else falls back, so a malformed or hostile body cannot put
 * arbitrary text in the error card.
 */
export function readRefusalEnvelope(
  status: number,
  body: string,
): { code: string; message: string } | null {
  if (status < 400 || status >= 500) return null;
  // 401 stays on the client's own story. This controller has already run its
  // single-flight refresh and the api-client has ended the session by the time
  // we get here, so "your session may have expired" is the true account of
  // what happened — not whatever the expired request said.
  if (status === 401) return null;
  if (!body) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(body);
  } catch {
    return null;
  }
  const err = (parsed as { error?: unknown } | null)?.error;
  if (!err || typeof err !== 'object') return null;
  const code = (err as { code?: unknown }).code;
  const message = (err as { message?: unknown }).message;
  if (typeof code !== 'string' || !/^[A-Z][A-Z0-9_]{2,63}$/.test(code)) return null;
  if (typeof message !== 'string') return null;
  const trimmed = message.trim();
  if (trimmed.length === 0 || trimmed.length > 300 || /[\n\r]/.test(trimmed)) {
    return null;
  }
  return { code, message: trimmed };
}

export function sanitizeHttpErrorMessage(status: number): string {
  if (status === 401 || status === 403) {
    return 'Your session may have expired or you no longer have access. Please sign in again.';
  }
  if (status === 429) {
    return 'Ask Argus is rate-limiting requests right now. Please wait a moment and try again.';
  }
  if (status >= 500) {
    return 'Ask Argus hit a server error. Retrying often clears it; if not, your admin can check the logs.';
  }
  return `Ask Argus request failed (status ${status}).`;
}

// ── Defaults ────────────────────────────────────────────────────────────────

export const DEFAULT_SAFETY_TIMEOUT_MS = 180_000;
/**
 * SSE reconnect is disabled by default (V1 safe-close, per issue #1323).
 *
 * The client sends `Last-Event-ID` on reconnect, but the server does not
 * yet support true SSE resume — it re-runs the whole query and re-emits
 * every token. Because the client appends `token` events into the same
 * message bubble, a reconnect can duplicate output the user has already
 * seen (the `BLUEBLUE` regression class).
 *
 * The safe behaviour is to fail closed: on a dropped stream, surface a
 * `SSE_RECONNECT_FAILED` error and let the user start a clean request.
 * That keeps the rendered transcript honest. Reconnect can be re-enabled
 * once the server emits a real resume protocol that lets the client
 * either resume from a known event id or start a fresh assistant
 * message bubble.
 *
 * Callers can opt back in by passing `maxReconnectAttempts: N` to the
 * controller — primarily used by unit tests that exercise the reconnect
 * loop machinery itself.
 */
export const DEFAULT_MAX_RECONNECT_ATTEMPTS = 0;
export const RECONNECT_BACKOFF_MS = [1_000, 2_000, 4_000];

// ── Controller (pure, non-React) ────────────────────────────────────────────

/**
 * Pure controller — owns dispatch, parser loop, reconnect machine.
 * The React hook below is a thin wrapper that mirrors the controller's
 * emitted snapshots into useState calls.
 *
 * Direct testing: instantiate, feed events via `handleEvent()`, or
 * exercise the full send() loop with a controllable `fetchImpl`.
 */

// CLAUDE.md §11.3 — "Responses inherit the highest source classification on
// both axes." Lattice mirrors the server (evaluator-automated.service.ts).
const SENS_ORDER = SENSITIVITIES;
const EXP_ORDER = EXPORT_CONTROLS_BY_STRICTNESS;

/**
 * Merge two classifications by taking the MOST RESTRICTIVE value on each axis.
 * Used because a multi-agent (@mention) turn emits several `metadata` events;
 * the banner must reflect the highest classification any agent returned, not
 * whichever happened to arrive last.
 */
function mergeHighestClassification(
  prev: { sensitivity: string; exportControl: string } | undefined,
  next: { sensitivity: string; exportControl: string },
): { sensitivity: string; exportControl: string } {
  if (!prev) return next;
  const si = Math.max(
    SENS_ORDER.indexOf(prev.sensitivity as (typeof SENS_ORDER)[number]),
    SENS_ORDER.indexOf(next.sensitivity as (typeof SENS_ORDER)[number]),
  );
  const ei = Math.max(
    EXP_ORDER.indexOf(prev.exportControl as (typeof EXP_ORDER)[number]),
    EXP_ORDER.indexOf(next.exportControl as (typeof EXP_ORDER)[number]),
  );
  return {
    sensitivity: SENS_ORDER[si] ?? next.sensitivity,
    exportControl: EXP_ORDER[ei] ?? next.exportControl,
  };
}

export class ChatStreamController {
  private readonly opts: UseChatStreamOptions;
  private abortController: AbortController | null = null;
  private safetyTimer: ReturnType<typeof setTimeout> | null = null;
  private bubbles: Map<string, AgentBubbleState> = new Map();
  private lastBubbleKey = '__default__';
  private timedOutFlag = false;
  private phase: StreamPhase = 'idle';
  private activePipelinePhaseLabel: string | undefined = undefined;
  private streaming = false;
  private activeToggles: ChatToggles | undefined = undefined;
  private activeClassification:
    | { sensitivity: string; exportControl: string }
    | undefined = undefined;
  private reconnectStateInternal: ReconnectState = 'idle';
  private currentRetryPrompt = '';
  /** Counter for generating bubble ids — avoids Date.now collisions. */
  private bubbleSeq = 0;

  constructor(opts: UseChatStreamOptions) {
    this.opts = opts;
  }

  /** Snapshot of public state for the React wrapper. */
  snapshot(): ChatStreamSnapshot {
    return {
      phase: this.phase,
      activePipelinePhaseLabel: this.activePipelinePhaseLabel,
      isStreaming: this.streaming,
      activeToggles: this.activeToggles,
      activeClassification: this.activeClassification,
      agentBubbles: this.bubbles as ReadonlyMap<
        string,
        Readonly<AgentBubbleState>
      >,
      reconnectState: this.reconnectStateInternal,
    };
  }

  private emit(): void {
    this.opts.onStateChange?.(this.snapshot());
  }

  private setPhase(next: StreamPhase): void {
    if (next === this.phase) return;
    this.phase = next;
    this.emit();
  }

  private setReconnect(next: ReconnectState, attempt: number): void {
    if (next !== this.reconnectStateInternal) {
      this.reconnectStateInternal = next;
      this.emit();
    }
    this.opts.onReconnectState?.(next, attempt);
  }

  private ensureBubble(
    bubbleKey: string,
    agentId: string | undefined,
    displayName: string | undefined,
    kind: string | undefined,
  ): AgentBubbleState {
    const existing = this.bubbles.get(bubbleKey);
    if (existing) {
      if (displayName && !existing.displayName) {
        existing.displayName = displayName;
      }
      if (kind && !existing.kind) {
        existing.kind = kind;
      }
      return existing;
    }
    this.bubbleSeq += 1;
    const bubbleId =
      bubbleKey === '__default__'
        ? `asst-${Date.now()}-${this.bubbleSeq}`
        : `asst-${Date.now()}-${this.bubbleSeq}-${bubbleKey.replace(
            /[^a-zA-Z0-9-]/g,
            '_',
          )}`;
    const state: AgentBubbleState = {
      key: bubbleKey,
      bubbleId,
      agentId,
      displayName,
      kind,
      content: '',
      citations: [],
      toolCalls: [],
      confidence: undefined,
      confidenceRationale: undefined,
      metadata: {},
      error: undefined,
      lastEventId: undefined,
    };
    this.bubbles.set(bubbleKey, state);
    this.lastBubbleKey = bubbleKey;
    this.opts.onAgentBubble?.({
      bubbleId,
      bubbleKey,
      agentId,
      displayName,
      kind,
      retryPrompt: this.currentRetryPrompt,
    });
    this.emit();
    return state;
  }

  /**
   * Public: process a single decoded SSE event. Exposed for unit
   * testing — the parser loop calls this internally.
   */
  handleEvent(evt: Record<string, unknown>): void {
    // Drive the phase machine first so visuals react to tool-call or
    // first token arrival immediately.
    const prevPhase = this.phase;
    const phaseLabel = extractPipelinePhaseLabel(evt);
    if (phaseLabel) this.activePipelinePhaseLabel = phaseLabel;
    const nextPhase = reduceStreamPhase(this.phase, evt as { type: string });
    this.setPhase(nextPhase);
    if (phaseLabel && nextPhase === prevPhase) this.emit();

    const rawAgentId = extractAgentId(evt);
    const bubbleKey = resolveBubbleKey(rawAgentId);
    const callbackAgentId =
      bubbleKey === '__default__' ? undefined : rawAgentId;
    const eventId = extractEventId(evt);

    const toggles = extractToggles(evt);
    if (toggles) {
      this.activeToggles = toggles;
      this.emit();
    }

    const type = evt.type as string | undefined;

    switch (type) {
      case 'metadata': {
        const d = (evt.data as Record<string, unknown> | undefined) ?? evt;
        const displayName =
          typeof d.agentDisplayName === 'string'
            ? (d.agentDisplayName as string)
            : undefined;
        const kind =
          typeof d.agentKind === 'string'
            ? (d.agentKind as string)
            : undefined;
        const state = this.ensureBubble(
          bubbleKey,
          callbackAgentId,
          displayName,
          kind,
        );
        if (eventId) state.lastEventId = eventId;
        if (typeof d.model === 'string') state.metadata.model = d.model;
        if (typeof d.intent === 'string') state.metadata.intent = d.intent;
        if (typeof d.brandId === 'string' && !state.metadata.model) {
          state.metadata.model = d.brandId;
        }
        let classification:
          | { sensitivity: string; exportControl: string }
          | undefined;
        const cls = d.classification as
          | { sensitivity?: unknown; exportControl?: unknown }
          | undefined;
        if (cls && typeof cls === 'object') {
          const sensitivity =
            typeof cls.sensitivity === 'string' ? cls.sensitivity : undefined;
          const exportControl =
            typeof cls.exportControl === 'string'
              ? cls.exportControl
              : undefined;
          if (sensitivity && exportControl) {
            classification = { sensitivity, exportControl };
            // §11.3 — keep the most-restrictive across all metadata events this
            // turn, not just the last one (multi-agent turns emit several).
            this.activeClassification = mergeHighestClassification(
              this.activeClassification,
              classification,
            );
          }
        }
        this.opts.onMetadata?.({
          bubbleId: state.bubbleId,
          bubbleKey,
          agentId: callbackAgentId,
          model: state.metadata.model,
          intent: state.metadata.intent,
          displayName: state.displayName,
          kind: state.kind,
          classification,
          toggles,
        });
        this.emit();
        break;
      }

      case 'token': {
        this.activePipelinePhaseLabel = undefined;
        const d = evt.data as { content?: unknown } | undefined;
        const c =
          d && typeof d.content === 'string'
            ? (d.content as string)
            : typeof (evt as { content?: unknown }).content === 'string'
              ? ((evt as { content?: unknown }).content as string)
              : undefined;
        if (typeof c !== 'string') break;
        const state = this.ensureBubble(
          bubbleKey,
          callbackAgentId,
          undefined,
          undefined,
        );
        state.content = state.content + c;
        if (eventId) state.lastEventId = eventId;
        this.opts.onToken?.({
          bubbleId: state.bubbleId,
          bubbleKey,
          agentId: callbackAgentId,
          delta: c,
          fullContent: state.content,
        });
        this.emit();
        break;
      }

      case 'tool-call':
      case 'tool_call': {
        const d = (evt.data as Record<string, unknown> | undefined) ?? evt;
        const state = this.ensureBubble(
          bubbleKey,
          callbackAgentId,
          undefined,
          undefined,
        );
        state.toolCalls.push(d as unknown as ToolCall);
        if (eventId) state.lastEventId = eventId;
        this.opts.onToolCall?.({
          bubbleId: state.bubbleId,
          bubbleKey,
          agentId: callbackAgentId,
          toolCall: d as ToolCall | Record<string, unknown>,
        });
        this.emit();
        break;
      }

      case 'citation':
      case 'citations': {
        const arr = extractCitations(evt);
        if (!Array.isArray(arr) || arr.length === 0) break;
        const state = this.ensureBubble(
          bubbleKey,
          callbackAgentId,
          undefined,
          undefined,
        );
        state.citations = [...state.citations, ...(arr as Citation[])];
        if (eventId) state.lastEventId = eventId;
        this.opts.onCitation?.({
          bubbleId: state.bubbleId,
          bubbleKey,
          agentId: callbackAgentId,
          citations: state.citations,
        });
        this.emit();
        break;
      }

      case 'pipeline': {
        const d =
          evt.data ??
          (evt as { stages?: unknown }).stages ??
          (evt as { pipeline?: unknown }).pipeline;
        if (!Array.isArray(d)) break;
        const state = this.ensureBubble(
          '__default__',
          undefined,
          undefined,
          undefined,
        );
        if (eventId) state.lastEventId = eventId;
        this.opts.onPipeline?.({
          bubbleId: state.bubbleId,
          bubbleKey: '__default__',
          stages: d as unknown[],
        });
        break;
      }

      case 'banner': {
        // bug #13 — answer-quality banner emitted by the backend gate for
        // unverified / contradicted answers. Attach to the default bubble.
        const d = (evt.data ?? evt) as {
          level?: unknown;
          kind?: unknown;
          message?: unknown;
        };
        const state = this.ensureBubble('__default__', undefined, undefined, undefined);
        if (eventId) state.lastEventId = eventId;
        this.opts.onBanner?.({
          bubbleId: state.bubbleId,
          bubbleKey: '__default__',
          level: typeof d.level === 'string' ? d.level : 'info',
          kind: typeof d.kind === 'string' ? d.kind : 'answer_quality',
          message: typeof d.message === 'string' ? d.message : '',
        });
        this.emit();
        break;
      }

      case 'follow_up':
      case 'follow-up': {
        // bug #23 — proactive follow-up suggestions. Emitted after `done`
        // with no bubbleId, so attach to the default bubble.
        const d = (evt.data ?? evt) as { suggestions?: unknown };
        const suggestions = Array.isArray(d.suggestions)
          ? d.suggestions.filter((s): s is string => typeof s === 'string')
          : [];
        if (suggestions.length === 0) break;
        const state = this.ensureBubble('__default__', undefined, undefined, undefined);
        if (eventId) state.lastEventId = eventId;
        this.opts.onFollowUp?.({
          bubbleId: state.bubbleId,
          bubbleKey: '__default__',
          suggestions,
        });
        this.emit();
        break;
      }

      case 'confidence': {
        const c = extractConfidence(evt);
        const state = this.ensureBubble(
          bubbleKey,
          callbackAgentId,
          undefined,
          undefined,
        );
        if (typeof c.confidence === 'number') state.confidence = c.confidence;
        if (typeof c.rationale === 'string') {
          state.confidenceRationale = c.rationale;
        }
        if (eventId) state.lastEventId = eventId;
        this.opts.onConfidence?.({
          bubbleId: state.bubbleId,
          bubbleKey,
          agentId: callbackAgentId,
          confidence: state.confidence,
          rationale: state.confidenceRationale,
        });
        this.emit();
        break;
      }

      case 'error': {
        this.activePipelinePhaseLabel = undefined;
        const err = normalizeErrorEvent(evt);
        const state = this.ensureBubble(
          bubbleKey,
          callbackAgentId,
          undefined,
          undefined,
        );
        state.error = err;
        if (eventId) state.lastEventId = eventId;
        this.opts.onError?.({
          bubbleId: state.bubbleId,
          bubbleKey,
          agentId: callbackAgentId,
          error: err,
          timedOut: false,
        });
        this.emit();
        break;
      }

      case 'done': {
        this.activePipelinePhaseLabel = undefined;
        const d = (evt.data as Record<string, unknown> | undefined) ?? evt;
        // bug #3 (ghost block) — the multi-agent route emits a FINAL aggregate
        // `done` with `multiAgent: true` and NO agent_id, AFTER each per-agent
        // bubble has already received its own agent_id-tagged `done`. That
        // untagged event resolves to `__default__`; calling ensureBubble() here
        // would mint a brand-new EMPTY default bubble (the turn ran entirely on
        // named-agent bubbles), surfacing as a stray empty 'Argus … N tokens'
        // block. When no `__default__` bubble already exists, treat the
        // aggregate done as a stream-finalize only — flip streaming off and
        // emit, without creating a bubble or re-firing onDone (the per-agent
        // done already did both for the real bubble).
        if (
          bubbleKey === '__default__' &&
          d.multiAgent === true &&
          !this.bubbles.has('__default__')
        ) {
          this.streaming = false;
          if (this.safetyTimer) {
            clearTimeout(this.safetyTimer);
            this.safetyTimer = null;
          }
          this.emit();
          break;
        }
        const state = this.ensureBubble(
          bubbleKey,
          callbackAgentId,
          undefined,
          undefined,
        );
        const totalTokens =
          typeof d.totalTokens === 'number'
            ? (d.totalTokens as number)
            : undefined;
        const latencyMs =
          typeof d.latencyMs === 'number'
            ? (d.latencyMs as number)
            : undefined;
        if (totalTokens !== undefined) {
          state.metadata.totalTokens = totalTokens;
        }
        if (latencyMs !== undefined) {
          state.metadata.latencyMs = latencyMs;
        }
        if (eventId) state.lastEventId = eventId;
        // fix-wave-4 #1054 — flip the composer back to interactive as
        // soon as the `done` event arrives. The parser loop's outer
        // await keeps running until the body closes, but the
        // user-visible state must move before the reader catches up.
        this.streaming = false;
        if (this.safetyTimer) {
          clearTimeout(this.safetyTimer);
          this.safetyTimer = null;
        }
        const serverMessageId =
          typeof d.messageId === 'string' && d.messageId.length > 0
            ? (d.messageId as string)
            : undefined;
        if (serverMessageId) {
          state.metadata.serverMessageId = serverMessageId;
        }
        this.opts.onDone?.({
          bubbleId: state.bubbleId,
          bubbleKey,
          agentId: callbackAgentId,
          totalTokens,
          latencyMs,
          messageId: serverMessageId,
        });
        this.emit();
        break;
      }

      case 'heartbeat':
      default:
        break;
    }
  }

  /** Reset all per-turn state. */
  private resetForNewTurn(): void {
    this.bubbles = new Map();
    this.phase = 'idle';
    this.activePipelinePhaseLabel = undefined;
    this.activeToggles = undefined;
    this.activeClassification = undefined;
    this.reconnectStateInternal = 'idle';
    this.lastBubbleKey = '__default__';
    this.timedOutFlag = false;
    this.bubbleSeq = 0;
    this.emit();
  }

  /**
   * Bearer for the next attempt: the api-client's proactive near-expiry
   * refresh first (same single-flight promise every JSON request shares),
   * falling back to the caller's `authToken()` when the helper is
   * unavailable or throws (e.g. no `localStorage` in unit tests).
   */
  private async resolveAccessToken(): Promise<string | null> {
    let fresh: string | null = null;
    try {
      fresh = await ensureFreshAccessToken();
    } catch {
      fresh = null;
    }
    return fresh ?? this.opts.authToken();
  }

  /**
   * The stream got a 401. One refresh via the api-client (which clears the
   * session + fires its onAuthError when no refresh is possible). Never
   * throws — null means "do not retry".
   */
  private async refreshAfterUnauthorized(): Promise<string | null> {
    try {
      return await handleStreamUnauthorized();
    } catch {
      return null;
    }
  }

  /**
   * Open the fetch, drain the SSE body, dispatch each event.
   * Returns `{ completed, retryable, reachedHeaders }` so the
   * reconnect loop can decide whether to attempt another open.
   */
  private async openStream(
    text: string,
    ctx: ChatSubmitContext,
    lastEventId: string | undefined,
  ): Promise<{
    completed: boolean;
    retryable: boolean;
    reachedHeaders: boolean;
  }> {
    const f = this.opts.fetchImpl ?? fetch;
    // Founder-reported 2026-09-03: the stream used to read the bearer
    // straight from `authToken()` and never refreshed it, so the first
    // question after an idle stretch hit a 401 card while a valid refresh
    // token sat in storage. Refresh proactively (single-flight, shared with
    // every other request) before the headers are built.
    const token = await this.resolveAccessToken();
    const controller = this.abortController!;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    if (token) headers.Authorization = `Bearer ${token}`;
    if (lastEventId) headers['Last-Event-ID'] = lastEventId;

    const body: Record<string, unknown> = {
      query: text,
      conversationId: ctx.conversationId,
      options: {
        ragEnabled: true,
        agentId: ctx.agentId || undefined,
      },
      toggles: ctx.toggles ?? undefined,
      brand: ctx.brand || undefined,
      ...ctx.extraBody,
    };
    if (ctx.attachedToolsSnapshot !== undefined) {
      body.attachedTools = ctx.attachedToolsSnapshot;
    }

    // Each attempt gets a shallow clone of the headers so a captured
    // `init.headers` stays an immutable snapshot after the retry rewrites
    // `Authorization` (mirrors `requestStream` in api-client.ts).
    const doFetch = (): Promise<Response> =>
      f(`/api/v1${this.opts.endpoint}`, {
        method: 'POST',
        headers: { ...headers },
        body: JSON.stringify(body),
        signal: controller.signal,
      });

    let res: Response;
    try {
      res = await doFetch();
      // 401 → one single-flight refresh and exactly ONE retry with the new
      // token. If the refresh fails the helper has already ended the
      // session; we fall through to the HTTP_401 card below.
      if (res.status === 401) {
        const refreshed = await this.refreshAfterUnauthorized();
        if (refreshed) {
          headers.Authorization = `Bearer ${refreshed}`;
          res = await doFetch();
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return { completed: false, retryable: false, reachedHeaders: false };
      }
      return { completed: false, retryable: true, reachedHeaders: false };
    }

    if (!res.ok || !res.body) {
      let raw: { status: number; body: string };
      try {
        const text = await res.text();
        raw = { status: res.status, body: text };
      } catch {
        raw = { status: res.status, body: '' };
      }
      // A pre-stream refusal our own routes AUTHOR carries its reason in the
      // body: `{ error: { code, message } }`. Discarding it and showing
      // "Ask Argus error (403)" is why the `WEB_SEARCH_DISABLED` refusal never
      // reached the user — the route says which switch is off and who can flip
      // it, and the client threw that away. `readRefusalEnvelope` adopts the
      // code and message ONLY from a well-formed 4xx envelope, so 5xx bodies
      // (which can carry a stack trace) still get the sanitized wording of
      // fix #745.
      const refusal = readRefusalEnvelope(res.status, raw.body);
      const state = this.ensureBubble(
        '__default__',
        undefined,
        undefined,
        undefined,
      );
      const error: ChatErrorState = {
        code: refusal?.code ?? `HTTP_${res.status}`,
        message: refusal?.message ?? sanitizeHttpErrorMessage(res.status),
        raw,
      };
      state.error = error;
      this.opts.onError?.({
        bubbleId: state.bubbleId,
        bubbleKey: '__default__',
        agentId: undefined,
        error,
        timedOut: false,
      });
      this.setPhase('error');
      return { completed: true, retryable: false, reachedHeaders: true };
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    // Cancel the reader when the AbortController fires. fetch wires
    // this up natively in the browser, but ReadableStream polyfills
    // (jsdom / node) don't always propagate, so we belt-and-suspenders
    // it here. This lets the parser loop's `read()` promise reject
    // promptly when the caller hits Stop.
    const onAbortCancelReader = () => {
      try {
        void reader.cancel(new DOMException('Aborted', 'AbortError'));
      } catch {
        // best effort
      }
    };
    if (controller.signal.aborted) {
      onAbortCancelReader();
    } else {
      controller.signal.addEventListener('abort', onAbortCancelReader, {
        once: true,
      });
    }

    try {
      // Keep reading until the stream closes naturally OR an abort
      // unwinds us out of the loop.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        if (controller.signal.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let sepIdx: number;
        while ((sepIdx = buffer.indexOf('\n\n')) !== -1) {
          const raw = buffer.slice(0, sepIdx);
          buffer = buffer.slice(sepIdx + 2);
          const dataLines = raw
            .split('\n')
            .filter((l) => l.startsWith('data:'))
            .map((l) => l.slice(5).trim());
          if (dataLines.length === 0) continue;
          const payload = dataLines.join('\n');
          let evt: Record<string, unknown>;
          try {
            evt = JSON.parse(payload);
          } catch {
            continue;
          }
          this.handleEvent(evt);
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return { completed: false, retryable: false, reachedHeaders: true };
      }
      // If the controller was aborted concurrently with a polyfill
      // read failure, surface that as a user-driven abort too — the
      // signal is the authoritative source of truth.
      if (controller.signal.aborted) {
        return { completed: false, retryable: false, reachedHeaders: true };
      }
      return { completed: false, retryable: true, reachedHeaders: true };
    } finally {
      try {
        controller.signal.removeEventListener(
          'abort',
          onAbortCancelReader as EventListener,
        );
      } catch {
        // ignore
      }
      // #14 — await the cancel so the underlying HTTP body is fully drained
      // before we return. The prior `void reader.cancel()` could leave the
      // response body unsettled (a slow leak across many aborted turns). The
      // loop has already exited here, so no read is in flight.
      try {
        await reader.cancel();
      } catch {
        // already closed / locked — nothing to drain
      }
    }

    return { completed: true, retryable: false, reachedHeaders: true };
  }

  /**
   * Submit a turn. Resolves when the stream terminates.
   */
  async send(text: string, ctx: ChatSubmitContext): Promise<void> {
    if (this.streaming) return;
    this.resetForNewTurn();
    this.streaming = true;
    this.setPhase('awaiting');
    this.currentRetryPrompt = text;
    this.timedOutFlag = false;

    const controller = new AbortController();
    this.abortController = controller;

    const safetyTimeoutMs =
      this.opts.safetyTimeoutMs ?? DEFAULT_SAFETY_TIMEOUT_MS;
    if (this.safetyTimer) clearTimeout(this.safetyTimer);
    this.safetyTimer = setTimeout(() => {
      if (!controller.signal.aborted) {
        this.timedOutFlag = true;
        try {
          (
            controller as AbortController & { _timedOut?: boolean }
          )._timedOut = true;
        } catch {
          // best effort
        }
        controller.abort();
      }
    }, safetyTimeoutMs);

    const maxAttempts =
      this.opts.maxReconnectAttempts ?? DEFAULT_MAX_RECONNECT_ATTEMPTS;
    let attempt = 0;

    try {
      // Reconnect loop.
      // eslint-disable-next-line no-constant-condition
      while (true) {
        let lastEventId: string | undefined;
        if (attempt > 0) {
          // Use the most recent event id across all bubbles. Server
          // is free to ignore (v1 behavior we accept).
          for (const b of this.bubbles.values()) {
            if (b.lastEventId) lastEventId = b.lastEventId;
          }
        }
        const result = await this.openStream(text, ctx, lastEventId);
        if (result.completed) return;
        if (controller.signal.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        if (!result.retryable) return;

        attempt += 1;
        if (attempt > maxAttempts) {
          this.setReconnect('failed', attempt);
          const state = this.ensureBubble(
            '__default__',
            undefined,
            undefined,
            undefined,
          );
          const error: ChatErrorState = {
            code: 'SSE_RECONNECT_FAILED',
            message:
              'Lost connection to Ask Argus and could not reconnect. Try again.',
          };
          state.error = error;
          this.opts.onError?.({
            bubbleId: state.bubbleId,
            bubbleKey: '__default__',
            agentId: undefined,
            error,
            timedOut: false,
          });
          this.setPhase('error');
          return;
        }
        this.setReconnect('reconnecting', attempt);
        const backoffMs =
          RECONNECT_BACKOFF_MS[
            Math.min(attempt - 1, RECONNECT_BACKOFF_MS.length - 1)
          ];
        await new Promise<void>((resolve, reject) => {
          const timer = setTimeout(resolve, backoffMs);
          const onAbortSig = () => {
            clearTimeout(timer);
            reject(new DOMException('Aborted', 'AbortError'));
          };
          controller.signal.addEventListener('abort', onAbortSig, {
            once: true,
          });
        });
        this.setReconnect('reconnecting', attempt);
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        // handled in finally
      } else {
        const state = this.ensureBubble(
          this.lastBubbleKey,
          this.lastBubbleKey === '__default__' ? undefined : this.lastBubbleKey,
          undefined,
          undefined,
        );
        const error: ChatErrorState = {
          message: err instanceof Error ? err.message : 'Stream interrupted',
          raw: err,
        };
        state.error = error;
        this.opts.onError?.({
          bubbleId: state.bubbleId,
          bubbleKey: state.key,
          agentId: state.agentId,
          error,
          timedOut: false,
        });
        this.setPhase('error');
      }
    } finally {
      if (controller.signal.aborted) {
        if (this.timedOutFlag) {
          const state = this.ensureBubble(
            '__default__',
            undefined,
            undefined,
            undefined,
          );
          const error: ChatErrorState = {
            code: 'SSE_TIMEOUT',
            message:
              'The response timed out before completing. The server stopped sending updates. Try again.',
          };
          state.error = error;
          this.opts.onError?.({
            bubbleId: state.bubbleId,
            bubbleKey: '__default__',
            agentId: undefined,
            error,
            timedOut: true,
          });
          this.setPhase('error');
        } else {
          const stoppedKey = this.lastBubbleKey ?? '__default__';
          const state = this.ensureBubble(
            stoppedKey,
            stoppedKey === '__default__' ? undefined : stoppedKey,
            undefined,
            undefined,
          );
          const existing = state.content;
          state.content =
            existing + (existing ? '\n\n' : '') + '_Generation stopped._';
          this.setPhase('done');
          this.opts.onAbort?.({
            bubbleId: state.bubbleId,
            bubbleKey: stoppedKey,
          });
          this.emit();
        }
      }
      if (this.safetyTimer) {
        clearTimeout(this.safetyTimer);
        this.safetyTimer = null;
      }
      this.abortController = null;
      this.streaming = false;
      if (this.reconnectStateInternal !== 'failed') {
        this.reconnectStateInternal = 'idle';
      }
      this.emit();
    }
  }

  /** Abort the in-flight stream. Idempotent. */
  abort(): void {
    const c = this.abortController;
    if (c && !c.signal.aborted) {
      c.abort();
    }
  }
}

// ── React hook wrapper ──────────────────────────────────────────────────────

/**
 * React hook wrapper around `ChatStreamController`. Mirrors the
 * controller's emitted snapshots into React state so the route's
 * render tree reacts to in-flight changes.
 */
export function useChatStream(opts: UseChatStreamOptions): UseChatStreamResult {
  const [snapshot, setSnapshot] = useState<ChatStreamSnapshot>(() => ({
    phase: 'idle',
    activePipelinePhaseLabel: undefined,
    isStreaming: false,
    activeToggles: undefined,
    activeClassification: undefined,
    agentBubbles: new Map<string, AgentBubbleState>() as ReadonlyMap<
      string,
      Readonly<AgentBubbleState>
    >,
    reconnectState: 'idle',
  }));

  // Keep callback identities stable across renders — the controller
  // closes over them on construction.
  const optsRef = useRef(opts);
  useEffect(() => {
    optsRef.current = opts;
  }, [opts]);

  const controllerRef = useRef<ChatStreamController | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = new ChatStreamController({
      ...opts,
      // Forward every callback through the ref so route updates pick
      // up the latest closure.
      onAgentBubble: (info) => optsRef.current.onAgentBubble?.(info),
      onMetadata: (e) => optsRef.current.onMetadata?.(e),
      onToken: (e) => optsRef.current.onToken?.(e),
      onToolCall: (e) => optsRef.current.onToolCall?.(e),
      onCitation: (e) => optsRef.current.onCitation?.(e),
      onConfidence: (e) => optsRef.current.onConfidence?.(e),
      onPipeline: (e) => optsRef.current.onPipeline?.(e),
      onBanner: (e) => optsRef.current.onBanner?.(e),
      onFollowUp: (e) => optsRef.current.onFollowUp?.(e),
      onError: (e) => optsRef.current.onError?.(e),
      onDone: (e) => optsRef.current.onDone?.(e),
      onAbort: (e) => optsRef.current.onAbort?.(e),
      onReconnectState: (s, a) => optsRef.current.onReconnectState?.(s, a),
      onStateChange: (snap) => {
        setSnapshot(snap);
        optsRef.current.onStateChange?.(snap);
      },
    });
  }

  const send = useCallback(
    (text: string, ctx: ChatSubmitContext) => {
      return controllerRef.current!.send(text, ctx);
    },
    [],
  );

  const abort = useCallback(() => {
    controllerRef.current?.abort();
  }, []);

  return {
    phase: snapshot.phase,
    activePipelinePhaseLabel: snapshot.activePipelinePhaseLabel,
    isStreaming: snapshot.isStreaming,
    activeToggles: snapshot.activeToggles,
    activeClassification: snapshot.activeClassification,
    agentBubbles: snapshot.agentBubbles,
    reconnectState: snapshot.reconnectState,
    send,
    abort,
  };
}
